import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Shield, Save, RotateCcw, Loader2 } from "lucide-react";
import AdminSelfServe2FA from "@/components/AdminSelfServe2FA";

const TYPE_LABELS = { int: "Number", bool: "Toggle", string: "Text" };

export default function AdminSecurityPage() {
  const [settings, setSettings] = useState([]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/security/settings");
      setSettings(data);
      setEdits({});
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async (key) => {
    const value = edits[key];
    if (value === undefined) return;
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await api.put(`/admin/security/settings/${key}`, { value });
      toast.success(`${key} updated`);
      setEdits(p => { const n = { ...p }; delete n[key]; return n; });
      await fetchSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(p => ({ ...p, [key]: false }));
    }
  };

  const handleReset = (key, defaultVal) => {
    setEdits(p => ({ ...p, [key]: defaultVal }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin text-[#888888]" />
      </div>
    );
  }

  // Group settings by category
  const groups = [
    { label: "Credit System", keys: ["initial_free_credits", "max_credit_cap", "max_send_per_alias", "receive_credit_value"] },
    { label: "Rate Limits", keys: ["send_rate_limit_window_minutes", "send_rate_limit_max_same_sender", "free_send_limit"] },
    { label: "Alias Limits", keys: ["alias_ttl_minutes", "max_free_aliases"] },
    { label: "Anti-Abuse Rules", keys: ["same_domain_credit_block_enabled", "self_send_credit_block_enabled", "disposable_domain_policy"] },
    { label: "Scoring Thresholds", keys: ["suspicious_score_threshold", "high_risk_score_threshold"] },
  ];

  const settingsMap = {};
  settings.forEach(s => { settingsMap[s.key] = s; });

  return (
    <ScrollArea className="h-full" data-testid="admin-security-page">
      <div className="max-w-3xl mx-auto p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center">
            <Shield size={20} className="text-[#555555]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[#111111]">Security Settings</h1>
            <p className="text-xs text-[#888888]">Global limits, thresholds, and anti-abuse rules</p>
          </div>
        </div>

        <AdminSelfServe2FA />

        <div className="space-y-8">
          {groups.map(group => (
            <div key={group.label} data-testid={`settings-group-${group.label.toLowerCase().replace(/\s/g, "-")}`}>
              <h2 className="text-sm font-semibold text-[#111111] mb-3 uppercase tracking-wide">{group.label}</h2>
              <div className="border border-[#E5E5E5] rounded-lg bg-white divide-y divide-[#E5E5E5]">
                {group.keys.map(key => {
                  const s = settingsMap[key];
                  if (!s) return null;
                  const editVal = edits[key];
                  const isDirty = editVal !== undefined && String(editVal) !== String(s.value);
                  const displayVal = editVal !== undefined ? editVal : s.value;

                  return (
                    <div key={key} className="flex items-center gap-4 px-4 py-3" data-testid={`setting-row-${key}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#111111]">{key.replace(/_/g, " ")}</p>
                        <p className="text-[11px] text-[#888888] mt-0.5">{s.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-[#888888] font-normal">{TYPE_LABELS[s.type] || s.type}</Badge>
                          {s.overridable && <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-blue-600 border-blue-200 font-normal">Overridable</Badge>}
                          {s.updated_by && s.updated_by !== "system" && (
                            <span className="text-[9px] text-[#AAAAAA]">by {s.updated_by}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {s.type === "bool" ? (
                          <Switch
                            checked={typeof displayVal === "boolean" ? displayVal : displayVal === "true" || displayVal === true}
                            onCheckedChange={(checked) => setEdits(p => ({ ...p, [key]: checked }))}
                            data-testid={`setting-toggle-${key}`}
                          />
                        ) : (
                          <Input
                            type={s.type === "int" ? "number" : "text"}
                            value={displayVal}
                            onChange={(e) => setEdits(p => ({ ...p, [key]: s.type === "int" ? Number(e.target.value) : e.target.value }))}
                            className="w-24 h-8 text-sm text-center"
                            data-testid={`setting-input-${key}`}
                          />
                        )}

                        {isDirty && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-[#888888]"
                              onClick={() => handleReset(key, s.default)}
                              title="Reset to default"
                            >
                              <RotateCcw size={13} />
                            </Button>
                            <Button
                              size="sm"
                              className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => handleSave(key)}
                              disabled={saving[key]}
                              data-testid={`setting-save-${key}`}
                            >
                              {saving[key] ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ScrollArea>
  );
}
